import { useLoginStore } from '@/login/stores/useLoginStore';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { QueryOptions, UseMutationOptions } from '@tanstack/vue-query';
import { queryOptions, useQueryClient } from '@tanstack/vue-query';
import type { AnnouncementType, UnseenAnnouncement } from '@sysarb/sysarb-types-v2/services/v1/notification';
import { unseenAnnouncementsValidator } from '@sysarb/sysarb-types-v2/services/v1/notification';

const notificationService = new RestManager('notification-service');

type AnnouncementsOptions = {
    queries: {
        getUnseenAnnouncements: (type?: AnnouncementType) => QueryOptions<UnseenAnnouncement[], Error>;
    };
    mutations: {
        markAnnouncementSeen: (type?: AnnouncementType) => UseMutationOptions<void, Error, string>;
    };
};

export const announcements: AnnouncementsOptions = {
    queries: {
        getUnseenAnnouncements: (type?: AnnouncementType): QueryOptions<UnseenAnnouncement[], Error> => {
            const { instanceTopic, userId } = useLoginStore();

            return queryOptions<UnseenAnnouncement[], Error>({
                queryKey: ['announcements', instanceTopic, userId, type],
                queryFn: async (): Promise<UnseenAnnouncement[]> => {
                    if (instanceTopic == null || userId == null) return [];
                    const query = type != null ? `?type=${type}` : '';
                    const data = await notificationService.get<unknown>(`v1/${instanceTopic}/users/${userId}/announcements${query}`);
                    return unseenAnnouncementsValidator.parse(data);
                },
                enabled: () => instanceTopic != null && userId != null,
            });
        },
    },
    mutations: {
        markAnnouncementSeen: (type?: AnnouncementType): UseMutationOptions<void, Error, string> => {
            const queryClient = useQueryClient();
            const { instanceTopic, userId } = useLoginStore();

            return {
                mutationFn: async (targetId: string): Promise<void> => {
                    if (instanceTopic == null || userId == null) return;
                    await notificationService.put<void>(undefined, `v1/${instanceTopic}/users/${userId}/announcements/${targetId}/read`);
                },
                onSettled: async (): Promise<void> => {
                    await queryClient.invalidateQueries({ queryKey: ['announcements', instanceTopic, userId, type] });
                },
            };
        },
    },
};
